import React, { useMemo } from 'react';
import {
  ScrollView,
  StyleSheet,
  RefreshControl,
  Dimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useHomeScreen } from '@/hooks/residents/useHomeScreen';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';

import {
  GreetingSection,
  BalanceDueCard,
  QuickActionsSection,
  RecentActivitySection,
  CommunityResourcesSection,
  TodoCalendarSection,
} from '@/components/residents/redesign';
import { LinkedAccountsCard } from '@/components';

import { DesktopHomeView } from './desktop';

const HP = REDESIGN_SPACING.horizontalPadding;

export const HomeScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopHomeView />;
  return <MobileHomeView />;
};

const MobileHomeView: React.FC = () => {
  const { colors } = useThemeColors();
  const insets = useSafeAreaInsets();
  const {
    user,
    greeting,
    firstName,
    unitName,
    propertyName,
    propertyImageUrl,
    recentActivity,
    linkedAccounts,
    newsfeedNewCount,
    unreadMessagesCount,
    validEvents,
    surveysToAnswerCount,
    balanceDue,
    autopayEnabled,
    openTickets,
    refreshing,
    shouldShowPayments,
    handleRefresh,
    handleBulletinPress,
    handleEventPress,
    handleEventsListPress,
    handleSurveysPress,
    handlePlannerPress,
    handleWiFiPress,
    handleLinkedAccountsPress,
    handleMarketplacePress,
    handleMakePayment,
    handleMaintenancePress,
    handleMessagesPress,
    handleActivityItemPress,
    handleResourcesPress,
  } = useHomeScreen();

  const leaseId = user?.currentLeaseId ? String(user.currentLeaseId) : '';
  const { accountCredit } = usePaymentBalance(leaseId);

  const mobileItemWidth = useMemo(() => {
    const screenWidth = Dimensions.get('window').width;
    return { width: (screenWidth - HP * 2 - REDESIGN_SPACING.gridGap) / 2 };
  }, []);

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        { paddingBottom: 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    >
      <GreetingSection
        greeting={greeting}
        firstName={firstName}
        unitName={unitName}
        hasImage={!!propertyImageUrl}
      />

      <TodoCalendarSection onViewAll={handlePlannerPress} />

      <QuickActionsSection
        openTicketsCount={openTickets?.length || 0}
        newsfeedNewCount={newsfeedNewCount}
        unreadMessagesCount={unreadMessagesCount}
        surveysCount={surveysToAnswerCount}
        onMaintenance={handleMaintenancePress}
        onNewsfeed={handleBulletinPress}
        onMessages={handleMessagesPress}
        onSurveys={handleSurveysPress}
        itemStyle={mobileItemWidth}
      />

      {shouldShowPayments && (
        <BalanceDueCard
          balanceDue={balanceDue}
          accountCredit={accountCredit}
          propertyName={propertyName}
          unitName={unitName}
          autopayEnabled={autopayEnabled}
          onMakePayment={handleMakePayment}
          style={styles.balanceDueCard}
        />
      )}

      <RecentActivitySection
        items={recentActivity}
        onItemPress={handleActivityItemPress}
      />

      <CommunityResourcesSection
        onMarketplace={handleMarketplacePress}
        onResources={handleResourcesPress}
        onEvents={() => {
          if (validEvents.length > 0) {
            handleEventPress(validEvents[0]);
          } else {
            handleEventsListPress();
          }
        }}
        onWifi={handleWiFiPress}
        itemStyle={mobileItemWidth}
      />

      <LinkedAccountsCard
        linkedAccounts={linkedAccounts}
        onPress={handleLinkedAccountsPress}
        style={styles.linkedAccountsCard}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: {
    paddingHorizontal: HP,
    gap: 0,
  },
  linkedAccountsCard: {
    marginTop: 20,
  },
  balanceDueCard: {
    marginTop: 20,
  },
});

export { HomeScreen as ResidentHomeScreen };
export default HomeScreen;
